import { type FC, type MouseEvent, useMemo } from 'react';
import {
  SizedCell,
  SortableHeadCell,
  TextEllipsisWithTooltip,
} from '__components/Kizen/Table';
import { useTranslation } from 'react-i18next';
import ActionCell from '__components/Tables/ActionCell';
import SharingSettingsCell from '__components/AccessRequests/Tables/Cells/SharingSettingsCell';
import styled from '@emotion/styled';
import { SERVERS_TABLE_ACTIONS } from 'pages/MCPServerSettings/constants';
import type { MCPServerConfigListItem } from '@kizen/api/mcp';

const EMPTY_VALUE = '—';

const StyledSharingSettingsCell = styled.div`
  display: flex;
  align-items: center;
`;

const StyledBadge = styled.span`
  display: inline-flex;
  align-items: center;
  flex: none;
  background-color: var(--color-background-dark);
  color: var(--color-background-white);
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 3px;
  margin-left: 6px;
  text-transform: uppercase;
  vertical-align: middle;
`;

const NameCellContainer = styled.span`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const NameCell: FC<{
  id: string;
  name: string;
  isSystemManaged: boolean;
  href: string;
  onClick: () => void;
}> = ({ id, name, isSystemManaged, href, onClick }) => {
  const { t } = useTranslation();
  return (
    <NameCellContainer>
      <TextEllipsisWithTooltip
        type="anchor"
        href={href}
        onClick={(e: MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          onClick();
        }}
        data-qa-id={`mcp-server-name-link-${id}`}
      >
        {name}
      </TextEllipsisWithTooltip>
      {isSystemManaged ? <StyledBadge>{t('Readonly')}</StyledBadge> : null}
    </NameCellContainer>
  );
};

const ControlsCell: FC<{
  data?: MCPServerConfigListItem;
  onSelectAction: (
    action: { value: keyof typeof SERVERS_TABLE_ACTIONS },
    data: MCPServerConfigListItem
  ) => void;
  canWriteSection: boolean;
  canDeleteSection: boolean;
}> = ({
  data,
  onSelectAction,
  canWriteSection,
  canDeleteSection,
  ...props
}) => {
  const { t } = useTranslation();
  const options = useMemo(() => {
    if (!canWriteSection) return [];
    return [
      {
        label: t('View MCP Server'),
        value: SERVERS_TABLE_ACTIONS.view,
      },
      canDeleteSection &&
        !data?.is_system_managed && {
          label: t('Delete'),
          value: SERVERS_TABLE_ACTIONS.delete,
        },
    ].filter(Boolean);
  }, [t, canWriteSection, canDeleteSection, data?.is_system_managed]);

  if (!canWriteSection) {
    return <SizedCell width="50px" padding="15px" />;
  }

  return (
    <ActionCell
      title={t('Select Action')}
      menuType="absolute"
      options={options}
      onSelectAction={onSelectAction}
      {...props}
      data={data}
      width="15px"
      padding="15px"
    />
  );
};

type UseColumnsProps = {
  onSelectAction: (
    action: { value: keyof typeof SERVERS_TABLE_ACTIONS },
    data: MCPServerConfigListItem
  ) => void;
  onClickName: (server: MCPServerConfigListItem) => void;
  onClickTools: (server: MCPServerConfigListItem) => void;
  onClickSharingSettings: (server: MCPServerConfigListItem) => void;
  canWriteSection: boolean;
  canDeleteSection: boolean;
};

export const useColumns = ({
  onSelectAction,
  onClickName,
  onClickTools,
  onClickSharingSettings,
  canWriteSection,
  canDeleteSection,
}: UseColumnsProps) => {
  const { t } = useTranslation();
  return useMemo(() => {
    const spacerColumn = {
      id: 'spacer-start',
      headCell: <SizedCell width="15px" />,
      cell: <SizedCell width="15px" />,
      format: () => <span />,
    };

    const nameColumn = {
      id: 'name',
      headCell: (
        <SortableHeadCell label={t('Name')} width="175px" padding="30px" />
      ),
      cell: <SizedCell width="200px" padding="30px" />,
      format: (_: string, data: MCPServerConfigListItem) => (
        <NameCell
          id={data.id}
          name={data.name}
          isSystemManaged={data.is_system_managed}
          href={`/mcp-servers/${data.id}`}
          onClick={() => onClickName(data)}
        />
      ),
    };

    const toolCountColumn = {
      id: 'tool_count',
      headCell: (
        <SortableHeadCell
          numeric
          label={t('Tools')}
          width="90px"
          padding="30px"
        />
      ),
      cell: <SizedCell width="90px" padding="30px" />,
      format: (count: number, data: MCPServerConfigListItem) => (
        <TextEllipsisWithTooltip
          type="anchor"
          href={`/mcp-servers/${data.id}/tools`}
          onClick={(e: MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            onClickTools(data);
          }}
        >
          {count ?? 0}
        </TextEllipsisWithTooltip>
      ),
    };

    const sharingSettingsColumn = {
      id: 'sharing_settings',
      headCell: (
        <SortableHeadCell
          hasSorting={false}
          label={t('Sharing Settings')}
          width="180px"
          padding="30px"
        />
      ),
      cell: <SizedCell width="180px" padding="30px" />,
      format: (_: unknown, data: MCPServerConfigListItem) => (
        <StyledSharingSettingsCell>
          <SharingSettingsCell
            data={data}
            access={data.employee_access}
            canEdit={canWriteSection}
            forceEditable={canWriteSection}
            onClickEdit={() => onClickSharingSettings(data)}
            hideRoles={['Admin', 'Edit']}
          />
        </StyledSharingSettingsCell>
      ),
    };

    if (!canWriteSection) {
      return [
        spacerColumn,
        nameColumn,
        {
          id: 'endpoint_url',
          headCell: (
            <SortableHeadCell
              hasSorting={false}
              label={t('Endpoint URL')}
              padding="30px"
            />
          ),
          cell: <SizedCell padding="30px" />,
          format: (endpointUrl: MCPServerConfigListItem['endpoint_url']) => (
            <TextEllipsisWithTooltip>
              {endpointUrl || EMPTY_VALUE}
            </TextEllipsisWithTooltip>
          ),
        },
        toolCountColumn,
        sharingSettingsColumn,
      ];
    }

    return [
      spacerColumn,
      nameColumn,
      {
        id: 'description',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Description')}
            width="230px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="230px" padding="30px" />,
        format: (description: MCPServerConfigListItem['description']) => (
          <TextEllipsisWithTooltip>
            {description || EMPTY_VALUE}
          </TextEllipsisWithTooltip>
        ),
      },
      toolCountColumn,
      sharingSettingsColumn,
      {
        id: 'is_enabled',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Status')}
            width="90px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="90px" padding="30px" />,
        format: (isEnabled: boolean | undefined) => (
          <TextEllipsisWithTooltip>
            {isEnabled ?? true ? t('Enabled') : t('Disabled')}
          </TextEllipsisWithTooltip>
        ),
      },
      {
        id: 'created',
        headCell: (
          <SortableHeadCell
            numeric
            label={t('Created')}
            width="120px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="120px" padding="30px" />,
        format: (created: string) => (
          <TextEllipsisWithTooltip>
            {created ? new Date(created).toLocaleDateString() : EMPTY_VALUE}
          </TextEllipsisWithTooltip>
        ),
      },
      {
        id: 'actions',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Actions')}
            width="110px"
            padding="15px"
            align="right"
          />
        ),
        cell: (
          <ControlsCell
            onSelectAction={onSelectAction}
            canWriteSection={canWriteSection}
            canDeleteSection={canDeleteSection}
          />
        ),
      },
    ];
  }, [
    onSelectAction,
    onClickName,
    onClickTools,
    onClickSharingSettings,
    canWriteSection,
    canDeleteSection,
    t,
  ]);
};
